import { useEffect, useRef, useState } from "react";
import { Download, FileText } from "lucide-react";
import { parseMindsetCustomerConfiguration, parseMindsetCustomerSdkRelease, parseMindsetCustomerSdkDownload, type MindsetCustomerSdkRelease, type MindsetCustomerSdkDownload } from "@mindset/api-client";
import type { IntegrationProvisioningClient } from "./integrationSetupClient";
import { buildIntegrationAgentHandoff } from "./integrationAgentHandoff";
import { fetchSdkArchive, sdkArchiveErrorMessage } from "./fetchSdkArchive";

interface Props {
  companyId: string; userId: string; applicationId: string;
  read: NonNullable<IntegrationProvisioningClient["sdkConfiguration"]>;
  environment?: "sandbox" | "production";
  readRelease?(companyId: string, userId: string, applicationId: string): Promise<MindsetCustomerSdkRelease>;
  createDownload?(companyId: string, userId: string, applicationId: string, input: { releaseId: string; assetId: "typescript-bundle" }): Promise<MindsetCustomerSdkDownload>;
}
export function IntegrationSdkDownload(props: Props) {
  return <DownloadControl key={JSON.stringify([props.companyId, props.userId, props.applicationId, props.environment])} {...props} />;
}
function DownloadControl({ companyId, userId, applicationId, environment, read, readRelease, createDownload }: Props) {
  const active = useRef(false), pending = useRef(false);
  const transfer = useRef<AbortController | null>(null);
  const [pendingAction, setPendingAction] = useState<"sdk" | "configuration" | "handoff" | null>(null);
  const busy = pendingAction !== null;
  const [error, setError] = useState("");
  const [release, setRelease] = useState<MindsetCustomerSdkRelease | null>(null);
  const [catalogStatus, setCatalogStatus] = useState("SDK ZIP unavailable.");
  useEffect(() => { active.current = true; return () => { active.current = false; transfer.current?.abort(); }; }, []);
  useEffect(() => {
    let current = true;
    setRelease(null);
    setCatalogStatus(readRelease && createDownload ? "Checking SDK availability..." : "SDK ZIP unavailable.");
    if (readRelease && createDownload) void readRelease(companyId, userId, applicationId).then(value => {
      const parsed = parseMindsetCustomerSdkRelease(value);
      if (current) { setRelease(parsed); setCatalogStatus(""); }
    }).catch(() => { if (current) setCatalogStatus("Approved SDK ZIP unavailable. Try again later."); });
    return () => { current = false; };
  }, [companyId, userId, applicationId, readRelease, createDownload]);
  async function downloadSdk() {
    if (pending.current || !release || !createDownload) return;
    pending.current = true; setPendingAction("sdk"); setError("");
    const controller = new AbortController();
    transfer.current = controller;
    try {
      const expected = { releaseId: release.releaseId, assetId: release.assets[0].assetId };
      const grant = parseMindsetCustomerSdkDownload(await createDownload(companyId, userId, applicationId, expected), expected);
      if (!active.current) return;
      const asset = release.assets[0];
      // Both values were strictly parsed, so the profile uniquely identifies its tuple.
      if (grant.schemaVersion !== release.schemaVersion ||
          (release.schemaVersion === 2 && (grant.schemaVersion !== 2 || grant.releaseProfile !== release.releaseProfile))) throw new Error();
      if (grant.fileName !== asset.fileName || grant.bytes !== asset.bytes || grant.sha256 !== asset.sha256) throw new Error();
      const archive = await fetchSdkArchive(grant, controller.signal);
      if (!active.current) return;
      const url = URL.createObjectURL(archive);
      const link = document.createElement("a");
      try {
        link.href = url; link.download = grant.fileName;
        link.rel = "noopener noreferrer"; link.referrerPolicy = "no-referrer";
        link.hidden = true; document.body.appendChild(link); link.click();
      } finally { link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    } catch (failure) {
      if (active.current) setError(sdkArchiveErrorMessage(failure));
    } finally {
      controller.abort();
      if (transfer.current === controller) transfer.current = null;
      pending.current = false;
      if (active.current) setPendingAction(null);
    }
  }
  async function download(handoff = false) {
    if (pending.current) return;
    pending.current = true; setPendingAction(handoff ? "handoff" : "configuration"); setError("");
    try {
      const configuration = parseMindsetCustomerConfiguration(await read(companyId, userId, applicationId));
      if (!active.current) return;
      if (configuration.applicationId !== applicationId || (environment && configuration.environment !== environment)) throw new Error();
      const content = handoff ? buildIntegrationAgentHandoff(configuration) : `${JSON.stringify(configuration, null, 2)}\n`;
      const url = URL.createObjectURL(new Blob([content], { type: handoff ? "text/markdown;charset=utf-8" : "application/json" }));
      const link = document.createElement("a");
      try {
        link.href = url;
        link.download = handoff ? `mindset-${configuration.environment}-agent-handoff.md` : `mindset-${configuration.environment}-configuration.json`;
        link.hidden = true; document.body.appendChild(link); link.click();
      } finally {
        link.remove();
        // Give the browser time to consume the object URL before releasing it.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch {
      if (active.current) setError("Configuration could not be downloaded. Verify activation and try again.");
    } finally {
      pending.current = false;
      if (active.current) setPendingAction(null);
    }
  }
  return <div className="integration-sdk-download">
    {release ? <p>{release.schemaVersion === 2 && release.releaseProfile === "web-android"
      ? "Supported: Web and Android. iOS is deferred and not supported in this release."
      : "Supported: Web, Android, and iOS."}</p> : null}
    <button type="button" className="portal-button" disabled={busy || !release || !createDownload} onClick={() => void downloadSdk()}>
      <Download size={16} aria-hidden="true" />{pendingAction === "sdk" ? "Preparing SDK ZIP..." : "Download SDK ZIP"}
    </button>
    {catalogStatus ? <p role="status">{catalogStatus}</p> : null}
    <button type="button" className="portal-button" disabled={busy} onClick={() => void download()}>
      <Download size={16} aria-hidden="true" />{pendingAction === "configuration" ? "Preparing configuration..." : "Download SDK configuration"}
    </button>
    <button type="button" className="portal-button" disabled={busy} onClick={() => void download(true)}>
      <FileText size={16} aria-hidden="true" />{pendingAction === "handoff" ? "Preparing agent handoff..." : "Download agent handoff"}
    </button>
    {error ? <p role="alert">{error}</p> : null}
  </div>;
}
